import { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import L from 'leaflet';
import { TROMSO_CENTER, mockTourGroups, mockVehicles } from './mockData';

interface TourGroup {
  id: string;
  name: string;
  guide: string;
  size: number;
  location: [number, number];
  status: 'active' | 'paused' | 'completed';
  startTime: string;
  duration: number; // in minutes
  trail: string;
  lastUpdate: string;
}

interface Vehicle {
  id: string;
  name: string;
  type: 'car' | 'buggy' | 'boat';
  driver: string;
  location: [number, number];
  status: 'active' | 'maintenance' | 'inactive';
  speed: number;
  fuel: number;
  lastUpdate: string;
}

export function LiveTracking() {
  const [selectedType, setSelectedType] = useState<'all' | 'tours' | 'vehicles'>('all');
  const [selectedItem, setSelectedItem] = useState<TourGroup | Vehicle | null>(null);
  const center: [number, number] = TROMSO_CENTER;

  const getMarkerIcon = (type: 'tour' | 'car' | 'buggy' | 'boat') => {
    const icon = type === 'tour' ? '👥' : 
                type === 'car' ? '🚙' : 
                type === 'buggy' ? '🛺' : '🚤';
    
    return L.divIcon({
      className: 'custom-marker',
      html: `<div class="w-8 h-8 rounded-full bg-white border-2 border-primary flex items-center justify-center">
        ${icon}
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  };

  return (
    <div className="grid grid-cols-[300px_1fr] gap-6">
      {/* Sidebar */}
      <div className="space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <Select value={selectedType} onValueChange={(value: 'all' | 'tours' | 'vehicles') => setSelectedType(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Show all" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Show All</SelectItem>
                <SelectItem value="tours">Tours Only</SelectItem>
                <SelectItem value="vehicles">Vehicles Only</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Active Tours */}
        {(selectedType === 'all' || selectedType === 'tours') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Active Tours</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {mockTourGroups.map(tour => (
                  <div
                    key={tour.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedItem?.id === tour.id ? 'bg-primary text-white' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedItem(tour as TourGroup)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{tour.name}</span>
                      <Badge variant={tour.status === 'active' ? 'default' : 'secondary'}>
                        {tour.status}
                      </Badge>
                    </div>
                    <div className="text-sm mt-1 opacity-80">
                      {tour.size} visitors • {tour.guide}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Vehicles */}
        {(selectedType === 'all' || selectedType === 'vehicles') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Active Vehicles</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {mockVehicles.map(vehicle => (
                  <div
                    key={vehicle.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedItem?.id === vehicle.id ? 'bg-primary text-white' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedItem(vehicle as Vehicle)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{vehicle.name}</span>
                      <Badge variant={
                        vehicle.status === 'active' ? 'default' :
                        vehicle.status === 'maintenance' ? 'secondary' : 'destructive'
                      }>
                        {vehicle.status}
                      </Badge>
                    </div>
                    <div className="text-sm mt-1 opacity-80">
                      {vehicle.type} • {vehicle.driver}
                    </div>
                    <div className="text-sm mt-1 opacity-80">
                      {vehicle.speed}km/h • {vehicle.fuel}% fuel
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Map */}
      <Card>
        <CardContent className="p-0">
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: '700px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            {mockTourGroups.map(tour => (
              <Marker
                key={tour.id}
                position={[tour.location[1], tour.location[0]]}
                icon={getMarkerIcon('tour')}
              >
                <Popup>
                  <div className="p-2">
                    <h4 className="font-semibold">{tour.name}</h4>
                    <p className="text-sm">Guide: {tour.guide}</p>
                    <p className="text-sm">Group Size: {tour.size}</p>
                    <p className="text-sm">Trail: {tour.trail}</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {mockVehicles.map(vehicle => (
              <Marker
                key={vehicle.id}
                position={[vehicle.location[1], vehicle.location[0]]}
                icon={getMarkerIcon(vehicle.type as 'boat' | 'car' | 'buggy')}
              >
                <Popup>
                  <div className="p-2">
                    <h4 className="font-semibold">{vehicle.name}</h4>
                    <p className="text-sm">Driver: {vehicle.driver}</p>
                    <p className="text-sm">Speed: {vehicle.speed}km/h</p>
                    <p className="text-sm">Fuel: {vehicle.fuel}%</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </CardContent>
      </Card>
    </div>
  );
}